// Single error type all services + UI consume. The fetch wrapper in client.js
// routes every non-2xx (and every network failure) through normalizeError so
// callers only ever catch APIError, never raw fetch shapes.

export class APIError extends Error {
  /**
   * @param {{ code?: string, message: string, fields?: Object<string,string>, status?: number, cause?: unknown }} init
   */
  constructor({ code = 'unknown', message, fields, status, cause }) {
    super(message);
    this.name = 'APIError';
    this.code = code;
    this.fields = fields;
    this.status = status;
    this.cause = cause;
  }

  static isAPIError(err) {
    return err instanceof APIError;
  }
}

/**
 * Normalise a fetch / unknown error into an APIError.
 * Accepts:
 *   - APIError                                 (passthrough)
 *   - { status, body }                         (the shape client.js throws after a non-2xx)
 *   - TypeError (fetch network failure)
 *   - any Error
 *
 * @param {unknown} err
 */
export function normalizeError(err) {
  if (APIError.isAPIError(err)) return err;

  if (err && typeof err === 'object' && 'status' in err && 'body' in err) {
    const { status, body } = err;
    if (body && typeof body === 'object') {
      return new APIError({
        code: body.code ?? body.error?.code ?? `http_${status}`,
        message: body.message ?? body.error?.message ?? 'Request failed',
        fields: body.fields ?? body.error?.fields,
        status,
        cause: err,
      });
    }
    return new APIError({
      code: `http_${status}`,
      message: typeof body === 'string' && body ? body : 'Request failed',
      status,
      cause: err,
    });
  }

  if (err instanceof TypeError && /fetch|network/i.test(err.message)) {
    return new APIError({ code: 'network', message: 'Network error.', cause: err });
  }

  if (err instanceof DOMException && err.name === 'AbortError') {
    return new APIError({ code: 'aborted', message: 'Request aborted.', cause: err });
  }

  if (err instanceof Error) {
    return new APIError({ code: 'unknown', message: err.message, cause: err });
  }

  return new APIError({ code: 'unknown', message: 'Unknown error' });
}
